/**
 * StudentTodaySummary.tsx
 * 출석부 사이드 패널 안에 들어가는 "오늘 종합" 섹션
 *
 * 2026-05-26: B작업 Step 1 — 회의에서 사용자가 요구한 흩어진 데이터 통합 표시.
 *   - 오늘 교시별 출결
 *   - 오늘 사유서 (제출/승인/거절)
 *   - 오늘 벌점
 *   - 최근 7일 순찰 노트
 *
 * 호출 API: GET /api/v1/owner/students/{student_id}/today-summary
 * (학생 정보·정기일정·입출입은 SeatDetailPanel이 이미 표시하므로 여기선 생략)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon, DocumentTextIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../../../services/authApi';
import { formatExceptionTime } from '../../../../utils/exceptionTime';

interface PeriodAttendance {
  period_index?: number;
  period_label?: string;
  state?: string | boolean;
  status?: string;
}

interface ExceptionItem {
  id: string;
  exception_type: string;
  status: string | null;
  expected_arrival_time: string | null;
  expected_departure_time: string | null;
  reason: string | null;
  submitter_role: string | null;
  created_at: string | null;
  school_override?: boolean;
}

interface PenaltyItem {
  id: string;
  penalty_type: string;
  points: number;
  reason: string;
  created_at: string | null;
  is_cancelled: boolean;
  cancelled_at: string | null;
  cancel_reason: string | null;
  is_auto: boolean;
  point_type: string | null;
}

interface PatrolNoteItem {
  id: string;
  note_type: string;
  note_text: string | null;
  star_effect: number;
  created_at: string | null;
  staff_name: string | null;
}

interface TodaySummaryResponse {
  student: any;
  target_date: string;
  period_attendance: PeriodAttendance[];
  regular_schedule: any[];
  exceptions: ExceptionItem[];
  access_logs: any[];
  patrol_notes: PatrolNoteItem[];
  penalties: PenaltyItem[];
  late_info: LateInfo | null;
}

// ⭐ 2026-06-14: 지각 정보 (출결과 분리) — 교시(블록)별·시간 기준
interface LatePeriod {
  period_number: number;
  period_label: string;
  expected: string;   // 기대 시각 (첫 교시=등록 도착시각, 이후=교시 시작)
  actual: string;     // 실제 복귀 시각
  minutes_late: number;
}
interface LateInfo {
  periods: LatePeriod[];
  total_late: number;
  max_minutes: number;
}

interface Props {
  studentId: string | null | undefined;
  targetDate?: string;  // YYYY-MM-DD, 미지정 시 오늘
}

const StudentTodaySummary: React.FC<Props> = ({ studentId, targetDate }) => {
  const [data, setData] = useState<TodaySummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);  // 기본 펼침

  const fetchSummary = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError(null);
    try {
      const params = targetDate ? `?target_date=${targetDate}` : '';
      // apiClient baseURL=/api 이므로 '/api' 빼야 함
      const res = await apiClient.get(`/v1/owner/students/${studentId}/today-summary${params}`);
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || '오늘 종합 데이터를 불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  }, [studentId, targetDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (!studentId) return null;

  // 사유서 상태 라벨 (badge = 상태 뱃지, card = 항목 배경)
  const exStatusLabel = (status: string | null): { text: string; color: string; card: string } => {
    switch (status) {
      case 'pending': return { text: '학부모 승인 대기', color: 'bg-yellow-100 text-yellow-800', card: 'bg-yellow-50 border-yellow-300' };
      case 'parent_approved': return { text: '운영자 승인 대기', color: 'bg-blue-100 text-blue-800', card: 'bg-blue-50 border-blue-300' };
      case 'approved': return { text: '승인됨', color: 'bg-green-100 text-green-800', card: 'bg-green-50 border-green-200' };
      case 'rejected': return { text: '거절됨', color: 'bg-red-100 text-red-800', card: 'bg-red-50 border-red-200' };
      case 'cancelled': return { text: '신청자 취소', color: 'bg-gray-200 text-gray-700', card: 'bg-gray-50 border-gray-200' };
      default: return { text: status || '-', color: 'bg-gray-100 text-gray-800', card: 'bg-gray-50 border-gray-200' };
    }
  };

  const exTimeText = (ex: ExceptionItem): string | null =>
    formatExceptionTime(ex.exception_type, ex.expected_arrival_time, ex.expected_departure_time);

  const submitterText = (role: string | null): string | null => {
    if (!role) return null;
    if (role === 'parent') return '학부모';
    if (role === 'student') return '학생';
    if (role === 'owner' || role === 'staff') return '원장';
    return role;
  };

  const activeExCount = data?.exceptions?.length ?? 0;
  const activePenaltyCount = data?.penalties?.filter((p) => !p.is_cancelled).length ?? 0;

  return (
    <div className="border border-gray-200 rounded-lg bg-white mb-3">
      {/* 헤더 */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-blue-50 border-b border-gray-200 rounded-t-lg hover:bg-blue-100 transition-colors"
      >
        <span className="font-semibold text-sm text-gray-800 flex items-center gap-2 flex-wrap">
          <ClipboardDocumentCheckIcon className="w-4 h-4" />
          오늘 종합 (출결·사유서·벌점·순찰)
          {/* 접어둬도 사유서·벌점이 있는지는 보이게 */}
          {activeExCount > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800">사유서 {activeExCount}</span>
          )}
          {activePenaltyCount > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-800">벌점 {activePenaltyCount}</span>
          )}
        </span>
        {expanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="p-3 text-sm">
          {loading && <div className="text-gray-500">불러오는 중...</div>}
          {error && (
            <div className="text-red-600 bg-red-50 border border-red-200 p-2 rounded">
              ⚠️ {error}
            </div>
          )}
          {!loading && !error && data && (
            <div className="space-y-4">
              {/* ⭐ 지각 (출결과 분리 — 교시별·시간 기준, 쉬는시간 복귀 늦음 포함) */}
              {data.late_info && data.late_info.total_late > 0 && (
                <section>
                  <h4 className="font-semibold text-xs text-gray-700 mb-1">🕐 지각 ({data.late_info.total_late}건)</h4>
                  <div className="space-y-1">
                    {data.late_info.periods.map((p) => (
                      <div key={p.period_number}
                           className="text-xs px-2 py-1.5 rounded border flex flex-wrap items-center gap-x-2 gap-y-0.5"
                           style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
                        <span className="font-semibold text-gray-800">{p.period_label}</span>
                        <span className="text-gray-500">예정 <span className="font-mono">{p.expected}</span> · 실제 <span className="font-mono">{p.actual}</span></span>
                        <span className="font-bold text-orange-600">{p.minutes_late}분 지각</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* 사유서 */}
              <section>
                <h4 className="font-semibold text-xs text-gray-700 mb-1 flex items-center gap-1">
                  <DocumentTextIcon className="w-3.5 h-3.5" /> 오늘 사유서 ({data.exceptions.length})
                </h4>
                {data.exceptions.length === 0 ? (
                  <div className="text-xs text-gray-400 px-2 py-1">없음</div>
                ) : (
                  <ul className="space-y-1">
                    {data.exceptions.map((ex) => {
                      const st = exStatusLabel(ex.status);
                      const timeText = exTimeText(ex);
                      const submitter = submitterText(ex.submitter_role);
                      return (
                        <li key={ex.id} className={`border rounded p-2 text-xs ${st.card}`}>
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className="font-semibold">
                              {ex.exception_type}
                              {timeText && <span className="ml-2 text-gray-600 font-normal">🕐 {timeText}</span>}
                            </span>
                            <span className="flex items-center gap-1 shrink-0">
                              {ex.school_override && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-100 text-orange-800">⚠ 강제 승인</span>
                              )}
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${st.color}`}>{st.text}</span>
                            </span>
                          </div>
                          {ex.reason && <div className="text-gray-700">사유: {ex.reason}</div>}
                          {submitter && <div className="text-[10px] text-gray-500 mt-0.5">제출: {submitter}</div>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* 벌점 */}
              <section>
                <h4 className="font-semibold text-xs text-gray-700 mb-1 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" /> 오늘 벌점 ({data.penalties.filter(p => !p.is_cancelled).length})
                </h4>
                {data.penalties.length === 0 ? (
                  <div className="text-xs text-gray-400 px-2 py-1">없음</div>
                ) : (
                  <ul className="space-y-1">
                    {data.penalties.map((p) => (
                      <li
                        key={p.id}
                        className={`border rounded p-2 text-xs ${p.is_cancelled ? 'bg-gray-50 border-gray-200 line-through text-gray-400' : 'bg-red-50 border-red-200'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">
                            {p.penalty_type} {p.points > 0 ? `+${p.points}` : p.points}점
                            {p.is_auto && <span className="ml-1 text-[10px] text-blue-600">[자동]</span>}
                          </span>
                          {p.is_cancelled && <span className="text-[10px]">취소됨</span>}
                        </div>
                        {p.reason && <div className="text-gray-600">{p.reason}</div>}
                        {p.is_cancelled && p.cancel_reason && (
                          <div className="text-[10px] text-gray-500 mt-0.5">취소 사유: {p.cancel_reason}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* 순찰 노트 */}
              {data.patrol_notes.length > 0 && (
                <section>
                  <h4 className="font-semibold text-xs text-gray-700 mb-1">📋 최근 7일 순찰 노트 ({data.patrol_notes.length})</h4>
                  <ul className="space-y-1">
                    {data.patrol_notes.slice(0, 5).map((n) => (
                      <li key={n.id} className="bg-gray-50 border border-gray-200 rounded p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{n.note_type}</span>
                          <span className="text-gray-500 text-[10px]">
                            {n.created_at?.slice(5, 16).replace('T', ' ')}
                            {n.staff_name && ` · ${n.staff_name}`}
                          </span>
                        </div>
                        {n.note_text && <div className="text-gray-600">{n.note_text}</div>}
                      </li>
                    ))}
                    {data.patrol_notes.length > 5 && (
                      <li className="text-xs text-gray-400 px-2">외 {data.patrol_notes.length - 5}건</li>
                    )}
                  </ul>
                </section>
              )}

              {/* Step 2 reserved */}
              <div className="text-[10px] text-gray-400 border-t border-gray-100 pt-2">
                추가 예정 (Step 2): 휴대폰 검사·메모·학부모 문자
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentTodaySummary;
